import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useFantasyTrades, useRespondToTrade, useFantasyRoster, useProposeTrade, useFantasyTransactions, useAddDropPlayer } from '../../hooks/useLeagues'
import PlayerDetailModal from './PlayerDetailModal'
import { useAuth } from '../../hooks/useAuth'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'
import { SkeletonCard } from '../ui/Skeleton'
import Avatar from '../ui/Avatar'
import { toast } from '../ui/Toast'
import PlayerHeadshot from '../ui/PlayerHeadshot'

// Explicit past-tense map — templating `${action}ed` gives "declineed" and
// `${action}d` gives "canceld" / "vetod". Kept in sync with the identical
// helper in FantasyMyTeam.jsx.
function pastTense(action) {
  return {
    accept: 'accepted',
    decline: 'declined',
    veto: 'vetoed',
    cancel: 'canceled',
  }[action] || `${action}d`
}

function PlayerChip({ player }) {
  return (
    <div className="flex flex-col items-center rounded-lg bg-bg-primary/60 border border-text-primary/10 px-2 py-3 text-center">
      {player?.headshot_url && (
        <img src={player.headshot_url} alt="" className="w-14 h-14 rounded-full object-cover bg-bg-secondary mb-1.5" onError={(e) => { e.target.style.display = 'none' }} />
      )}
      <div className="text-sm font-bold text-text-primary leading-tight">{player?.full_name}</div>
      <div className="text-[11px] text-text-muted mt-0.5">{player?.position} · {player?.team}</div>
    </div>
  )
}

function TradeCard({ trade, currentUserId, isCommissioner, onAccept, onDecline, onCancel, onCounter, onApprove, onVeto }) {
  const isReceiver = trade.receiver_user_id === currentUserId
  const isProposer = trade.proposer_user_id === currentUserId
  const isPending = trade.status === 'pending'
  const isPendingReview = trade.status === 'pending_review'

  const proposerItems = (trade.fantasy_trade_items || []).filter((i) => i.from_user_id === trade.proposer_user_id)
  const receiverItems = (trade.fantasy_trade_items || []).filter((i) => i.from_user_id === trade.receiver_user_id)

  // Status colors are text-only (no background tints) since the status word
  // now sits as plain text above the trade arrow rather than as a pill.
  const statusColors = {
    pending: 'text-yellow-500',
    pending_review: 'text-accent',
    accepted: 'text-correct',
    declined: 'text-incorrect',
    cancelled: 'text-text-muted',
    countered: 'text-text-primary',
    vetoed: 'text-incorrect',
  }

  const statusLabels = {
    pending_review: 'awaiting approval',
  }

  return (
    <div className={`rounded-xl border border-text-primary/20 p-4 bg-bg-primary/40`}>
      {/* Top row: avatars sit centered over their respective player columns
          (left = proposer, right = receiver). Status word + arrow stack
          centered in the middle so the status is visually tied to the
          direction of the trade. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center mb-4">
        <div className="flex justify-center">
          <Avatar user={trade.proposer} size="2xl" />
        </div>
        <div className="flex flex-col items-center px-3">
          <span className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${statusColors[trade.status] || ''}`}>
            {statusLabels[trade.status] || trade.status}
          </span>
          <svg className="w-7 h-7 text-text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4-4 4M3 12h18" />
          </svg>
        </div>
        <div className="flex justify-center">
          <Avatar user={trade.receiver} size="2xl" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm font-semibold text-text-primary mb-1.5">{trade.proposer?.display_name || 'Proposer'} sends</div>
          <div className="space-y-1">{proposerItems.map((i) => <PlayerChip key={i.player_id} player={i.nfl_players} />)}</div>
        </div>
        <div>
          <div className="text-sm font-semibold text-text-primary mb-1.5">{trade.receiver?.display_name || 'Receiver'} sends</div>
          <div className="space-y-1">{receiverItems.map((i) => <PlayerChip key={i.player_id} player={i.nfl_players} />)}</div>
        </div>
      </div>

      {trade.message && <div className="text-sm text-text-primary italic mt-3">"{trade.message}"</div>}

      {trade.status === 'vetoed' && trade.veto_reason && (
        <div className="mt-3 rounded-lg border border-incorrect/30 bg-incorrect/10 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-incorrect font-semibold mb-0.5">Commissioner reason</div>
          <div className="text-sm text-text-primary">{trade.veto_reason}</div>
        </div>
      )}

      {isPending && isReceiver && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onAccept(trade.id)} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-correct text-white">Accept</button>
          {onCounter && (
            <button onClick={() => onCounter(trade)} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors">Counter</button>
          )}
          <button onClick={() => onDecline(trade.id)} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-incorrect text-white">Decline</button>
        </div>
      )}
      {isPending && isProposer && (
        <button onClick={() => onCancel(trade.id)} className="w-full mt-3 py-2 rounded-lg text-xs font-semibold text-text-muted border border-text-primary/20 hover:bg-text-primary/5">Cancel</button>
      )}
      {isPendingReview && isCommissioner && (
        <div className="flex gap-3 mt-3">
          <button onClick={() => onApprove?.(trade.id)} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-correct text-white">Approve</button>
          <button onClick={() => onVeto?.(trade.id)} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-incorrect text-white">Veto</button>
        </div>
      )}
      {isPending && isCommissioner && !isReceiver && !isProposer && (
        <button onClick={() => onVeto?.(trade.id)} className="w-full mt-3 py-2 rounded-lg text-xs font-semibold text-incorrect border border-incorrect/30 hover:bg-incorrect/10">Veto</button>
      )}
    </div>
  )
}

// =====================================================================
// Transaction Log Item
// =====================================================================

function formatTimeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function TransactionRow({ txn, onTapPlayer }) {
  const player = txn.nfl_players || {}
  const user = txn.users || {}

  const typeConfig = {
    add: { icon: '+', color: 'text-correct', label: 'added' },
    drop: { icon: '-', color: 'text-incorrect', label: 'dropped' },
    // "acquired X through waivers" rather than "claimed (waiver) X" — reads
    // as a sentence instead of a log line with a parenthetical type tag.
    waiver_add: { icon: '+', color: 'text-correct', label: 'acquired', suffix: 'through waivers' },
    waiver_drop: { icon: '-', color: 'text-incorrect', label: 'dropped' },
    draft: { icon: '\u2605', color: 'text-text-primary', label: 'drafted' },
  }
  const cfg = typeConfig[txn.type] || { icon: '?', color: 'text-text-muted', label: txn.type }

  const handleTap = () => {
    if (onTapPlayer && txn.player_id) onTapPlayer(txn.player_id, txn.type)
  }

  return (
    <div className="flex items-center gap-3 py-3.5 border-b border-text-primary/10 last:border-0">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${cfg.color}`}>
        {cfg.icon}
      </span>
      <button type="button" onClick={handleTap} className="shrink-0">
        {/* Shared component: falls back to initials rather than an empty grey
            disc. The old fallback rendered a blank circle when a player had
            no headshot, and the inline <img> only hid itself on error — so a
            broken URL left nothing at all. */}
        <PlayerHeadshot
          name={player.full_name}
          url={player.headshot_url}
          size="md"
          className="hover:ring-2 hover:ring-accent/40 transition-all"
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-base text-text-primary">
          <span className="font-semibold">{user.display_name || user.username}</span>
          {' '}<span className="text-text-primary/60">{cfg.label}</span>{' '}
          <span className="font-semibold">{player.full_name}</span>
          {cfg.suffix && <span className="text-text-primary/60">{' '}{cfg.suffix}</span>}
        </div>
        <div className="text-xs text-text-muted">
          {player.position} · {player.team || 'FA'}
          {txn.bid_amount > 0 && <span> · ${txn.bid_amount} FAAB</span>}
          {' · '}{formatTimeAgo(txn.created_at)}
        </div>
      </div>
    </div>
  )
}

function TradeTransactionRow({ items, timestamp }) {
  // Group by direction: sends and receives per user
  const sends = items.filter((t) => t.type === 'trade_send')
  const receives = items.filter((t) => t.type === 'trade_receive')

  // Build readable trade description: each user and what they acquired
  // Group receives by user
  const byReceiver = {}
  for (const r of receives) {
    const name = r.users?.display_name || r.users?.username || 'Unknown'
    if (!byReceiver[name]) byReceiver[name] = []
    byReceiver[name].push(r.nfl_players?.full_name || 'a player')
  }

  const parts = Object.entries(byReceiver).map(([name, players]) =>
    `${name} acquires ${players.join(' and ')}`
  )

  return (
    <div className="py-3.5 border-b border-text-primary/10 last:border-0">
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center text-lg font-bold shrink-0 text-accent">
          {'\u21C4'}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-accent uppercase tracking-wide mb-0.5">Trade Approved</div>
          <div className="text-sm text-text-primary">{parts.join('; ')}</div>
          <div className="text-[10px] text-text-muted mt-0.5">{formatTimeAgo(timestamp)}</div>
        </div>
      </div>
    </div>
  )
}

// =====================================================================
// Propose Trade Modal (exported for reuse from RosterModal)
// =====================================================================

// Canonical lineup order: starters in standard sequence, then bench, then IR.
// Numeric suffix (rb1/rb2/wr1/wr2/wr3, bench1, bench2, ir1) preserves the
// commissioner-set slot ordering.
function rosterSlotOrder(slot) {
  if (!slot) return 999
  const s = String(slot).toLowerCase()
  const tail = parseInt(s.replace(/^[a-z]+/, ''), 10) || 0
  if (s === 'qb') return 0
  if (s.startsWith('rb')) return 100 + tail
  if (s.startsWith('wr')) return 200 + tail
  if (s === 'te') return 300
  if (s === 'flex') return 400
  if (s === 'superflex' || s === 'sflex') return 500
  if (s === 'k') return 600
  if (s === 'def') return 700
  if (s.startsWith('bench')) return 1000 + tail
  if (s.startsWith('ir')) return 2000 + tail
  return 9999
}

function sortRosterByLineup(roster) {
  return [...(roster || [])].sort((a, b) => rosterSlotOrder(a.slot) - rosterSlotOrder(b.slot))
}

export function ProposeTradeModal({ league, currentUserId, onClose, initialReceiverId, initialAcquirePlayerId, counteringTradeId }) {
  const { data: myRoster } = useFantasyRoster(league.id)
  const propose = useProposeTrade(league.id)
  const respond = useRespondToTrade(league.id)

  const otherMembers = (league.members || []).filter((m) => m.user_id !== currentUserId)
  const [receiverId, setReceiverId] = useState(initialReceiverId || '')
  const [myPlayerIds, setMyPlayerIds] = useState([])
  const [theirPlayerIds, setTheirPlayerIds] = useState(initialAcquirePlayerId ? [initialAcquirePlayerId] : [])
  const [message, setMessage] = useState('')
  // Set when propose fails with requires_drop — opens the drop modal so
  // the proposer can select conditional drops, then re-fires propose with
  // proposer_drop_player_ids included.
  const [proposeDropModal, setProposeDropModal] = useState(null) // { dropsNeeded } | null

  // Lock body scroll while the modal is open. `overflow:hidden` alone isn't
  // enough on iOS WebKit — momentum scrolling still leaks through. Pinning
  // body to fixed at the current scrollY blocks it reliably; on cleanup we
  // restore both styles and scroll back to where we were.
  useEffect(() => {
    const scrollY = window.scrollY
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    }
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    return () => {
      document.body.style.overflow = prev.overflow
      document.body.style.position = prev.position
      document.body.style.top = prev.top
      document.body.style.width = prev.width
      window.scrollTo(0, scrollY)
    }
  }, [])

  const { data: theirRoster } = useQuery({
    queryKey: ['leagues', league.id, 'fantasy', 'roster', receiverId],
    queryFn: () => api.get(`/leagues/${league.id}/fantasy/roster/${receiverId}`),
    enabled: !!receiverId,
  })

  function toggle(arr, setter, id) {
    if (arr.includes(id)) setter(arr.filter((x) => x !== id))
    else setter([...arr, id])
  }

  async function handleSubmit(proposerDropPlayerIds) {
    if (!receiverId) { toast('Pick a trade partner', 'error'); return }
    if (myPlayerIds.length === 0 && theirPlayerIds.length === 0) { toast('Add at least one player', 'error'); return }
    try {
      // When countering, mark the original trade as countered first so it
      // doesn't sit alongside the new one as still-pending. Notification
      // is suppressed there; proposeTrade fires a single counter-aware
      // notification instead. Only fire this once — on a retry after the
      // drop modal, the original is already countered.
      if (counteringTradeId && !proposerDropPlayerIds) {
        await respond.mutateAsync({ tradeId: counteringTradeId, action: 'counter' })
      }
      await propose.mutateAsync({
        receiver_user_id: receiverId,
        proposer_player_ids: myPlayerIds,
        receiver_player_ids: theirPlayerIds,
        message: message.trim() || undefined,
        counters_trade_id: counteringTradeId || undefined,
        proposer_drop_player_ids: proposerDropPlayerIds || undefined,
      })
      toast(counteringTradeId ? 'Counter proposed' : 'Trade proposed', 'success')
      onClose()
    } catch (err) {
      const body = err?.response || err
      // Proposer would exceed roster cap — open the drop modal so they can
      // pre-commit conditional drops that fire at approval time.
      if (body?.requires_drop && body?.side === 'proposer') {
        setProposeDropModal({ dropsNeeded: body.drops_needed || 1 })
        return
      }
      toast(err.message || 'Failed to propose trade', 'error')
    }
  }

  // Render through a portal so the modal escapes any ancestor that has
  // transform/filter/will-change applied — those contain position:fixed
  // and would otherwise bound the modal inside the page content (clipped
  // by top navbar and bottom tab bar).
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-primary border border-text-primary/20 w-full max-w-md md:max-w-2xl rounded-2xl max-h-[85vh] flex flex-col overscroll-contain" onClick={(e) => e.stopPropagation()}>
        {/* Header — fixed at top */}
        <div className="shrink-0 bg-bg-primary border-b border-text-primary/10 px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <h3 className="font-display text-lg">{counteringTradeId ? 'Counter Trade' : 'Propose Trade'}</h3>
          <button onClick={onClose} className="text-text-muted p-1">&times;</button>
        </div>

        {/* Body — scrolls when content overflows */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-xs uppercase text-text-muted mb-2">Trade with</label>
            {counteringTradeId ? (
              // Counter trades are locked to the original proposer — render
              // them as a non-interactive row so the picker can't accidentally
              // swap who's getting countered.
              (() => {
                const locked = otherMembers.find((m) => m.user_id === receiverId)
                if (!locked) return null
                return (
                  <div className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-accent bg-accent/10">
                    <Avatar user={locked.users || locked} size="sm" />
                    <span className="text-sm font-semibold flex-1 min-w-0 truncate">{locked.users?.display_name || locked.users?.username || locked.username}</span>
                  </div>
                )
              })()
            ) : (
              <div className="flex flex-col gap-2">
                {otherMembers.map((m) => (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => { setReceiverId(m.user_id); setTheirPlayerIds([]) }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                      receiverId === m.user_id ? 'border-accent bg-accent/10' : 'border-text-primary/20 bg-bg-primary hover:bg-bg-card-hover'
                    }`}
                  >
                    <Avatar user={m.users || m} size="sm" />
                    <span className="text-sm font-semibold flex-1 min-w-0 truncate">{m.users?.display_name || m.users?.username || m.username}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {receiverId && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase text-text-muted mb-1.5">You give ({myPlayerIds.length})</div>
                <div className="space-y-1">
                  {sortRosterByLineup(myRoster).map((r) => {
                    const selected = myPlayerIds.includes(r.player_id)
                    return (
                      <button key={r.id} type="button" onClick={() => toggle(myPlayerIds, setMyPlayerIds, r.player_id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left transition-colors ${selected ? 'border-accent bg-accent/10' : 'border-border bg-bg-card hover:bg-bg-card-hover'}`}>
                        {r.nfl_players?.headshot_url && <img src={r.nfl_players.headshot_url} alt="" className="w-7 h-7 rounded-full object-cover bg-bg-secondary shrink-0" onError={(e) => { e.target.style.display = 'none' }} />}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold truncate">{r.nfl_players?.full_name}</div>
                          <div className="text-[10px] text-text-muted">{r.nfl_players?.position} · {r.nfl_players?.team}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-text-muted mb-1.5">You get ({theirPlayerIds.length})</div>
                <div className="space-y-1">
                  {sortRosterByLineup(theirRoster).map((r) => {
                    const selected = theirPlayerIds.includes(r.player_id)
                    return (
                      <button key={r.id} type="button" onClick={() => toggle(theirPlayerIds, setTheirPlayerIds, r.player_id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left transition-colors ${selected ? 'border-accent bg-accent/10' : 'border-border bg-bg-card hover:bg-bg-card-hover'}`}>
                        {r.nfl_players?.headshot_url && <img src={r.nfl_players.headshot_url} alt="" className="w-7 h-7 rounded-full object-cover bg-bg-secondary shrink-0" onError={(e) => { e.target.style.display = 'none' }} />}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold truncate">{r.nfl_players?.full_name}</div>
                          <div className="text-[10px] text-text-muted">{r.nfl_players?.position} · {r.nfl_players?.team}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {receiverId && (
            <div>
              <label className="block text-xs uppercase text-text-muted mb-1.5">Optional message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Pitch your trade…" rows={2} maxLength={280}
                className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent resize-none" />
            </div>
          )}
        </div>

        {/* Footer — pinned at bottom, always visible */}
        <div className="shrink-0 bg-bg-primary border-t border-text-primary/10 p-4 rounded-b-2xl">
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-border">Cancel</button>
            <button onClick={() => handleSubmit()} disabled={propose.isPending || respond.isPending || !receiverId}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-accent text-white disabled:opacity-50">
              {(propose.isPending || respond.isPending) ? 'Proposing…' : (counteringTradeId ? 'Propose Counter' : 'Propose Trade')}
            </button>
          </div>
        </div>
      </div>
      {proposeDropModal && (
        <TradeDropModal
          roster={myRoster}
          // Virtual trade — only the fantasy_trade_items array is used, to
          // exclude players involved in this trade from the drop options.
          trade={{ fantasy_trade_items: myPlayerIds.map((pid) => ({ player_id: pid })) }}
          dropsNeeded={proposeDropModal.dropsNeeded}
          onCancel={() => setProposeDropModal(null)}
          isPending={propose.isPending}
          title={`Drop ${proposeDropModal.dropsNeeded} Player${proposeDropModal.dropsNeeded > 1 ? 's' : ''} to Propose`}
          description={`This trade would put you over the roster cap. Pick ${proposeDropModal.dropsNeeded} to conditionally drop — they'll only be dropped if this trade is accepted and approved.`}
          confirmLabel="Conditionally Drop & Propose"
          pendingLabel="Proposing..."
          onConfirm={(dropIds) => {
            setProposeDropModal(null)
            handleSubmit(dropIds)
          }}
        />
      )}
    </div>,
    document.body
  )
}

// =====================================================================
// Drop Player Modal (shown when accepting a trade that would overflow roster)
// =====================================================================

export function TradeDropModal({ roster, trade, dropsNeeded, onConfirm, onCancel, isPending, title, description, confirmLabel, pendingLabel, includeIr = false }) {
  const [selected, setSelected] = useState([])

  // Players eligible to drop: user's roster minus players involved in the trade,
  // ordered the same way as the My Team tab (starter slot order → bench) so the
  // list feels like their own roster instead of a random alphabet.
  const tradePlayerIds = new Set((trade?.fantasy_trade_items || []).map((i) => i.player_id))
  // IR players are excluded for trades — they aren't part of the incoming
  // package and dropping one doesn't create the room a trade needs.
  //
  // includeIr flips that for the ineligible-IR flow, where the healed player
  // IS a legitimate choice: a manager who doesn't rate him should be able to
  // cut him rather than someone better.
  const droppable = sortRosterByLineup(
    (roster || []).filter((r) => !tradePlayerIds.has(r.player_id) && (includeIr || r.slot !== 'ir'))
  )

  function toggle(pid) {
    setSelected((prev) =>
      prev.includes(pid) ? prev.filter((id) => id !== pid) : prev.length < dropsNeeded ? [...prev, pid] : prev
    )
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      style={{
        paddingTop: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-top) + 1rem))',
        paddingBottom: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-bottom) + 1rem))',
      }}
      // When this modal is stacked over ProposeTradeModal, a backdrop click
      // bubbles up through the DOM — without stopPropagation it would also
      // trigger the propose modal's onClose, collapsing both modals from
      // one tap. Stop the bubble so only the drop modal closes.
      onClick={(e) => { e.stopPropagation(); onCancel() }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-bg-primary border border-text-primary/20 rounded-2xl w-full max-w-md max-h-full overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-text-primary/10">
          <h3 className="text-base font-bold text-text-primary">{title || `Drop ${dropsNeeded} Player${dropsNeeded > 1 ? 's' : ''} to Accept`}</h3>
          <p className="text-sm text-text-primary/70 mt-1">
            {description || `This trade adds more players than it sends. Select ${dropsNeeded} player${dropsNeeded > 1 ? 's' : ''} to drop.`}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {droppable.map((r) => {
            const p = r.nfl_players || {}
            const isSelected = selected.includes(r.player_id)
            return (
              <button
                key={r.player_id}
                onClick={() => toggle(r.player_id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  isSelected ? 'bg-incorrect/15 border border-incorrect/40' : 'hover:bg-text-primary/5 border border-transparent'
                }`}
              >
                {p.headshot_url ? (
                  <img src={p.headshot_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-bg-secondary shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary truncate">{p.full_name}</div>
                  <div className="text-xs text-text-muted">{p.position} · {p.team || 'FA'}</div>
                </div>
                {isSelected && (
                  <span className="text-xs font-bold text-incorrect">DROP</span>
                )}
              </button>
            )
          })}
        </div>
        <div className="p-4 border-t border-text-primary/10 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-text-primary/20">Cancel</button>
          <button
            onClick={() => onConfirm(selected)}
            disabled={selected.length !== dropsNeeded || isPending}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-correct text-white disabled:opacity-50"
          >
            {isPending ? (pendingLabel || 'Accepting...') : (confirmLabel || 'Drop & Accept')}
          </button>
        </div>
      </div>
    </div>
  )
}

function TransactionsOnboarding({ fantasySettings }) {
  const isFaab = fantasySettings?.waiver_type === 'faab'
  const isCommReview = fantasySettings?.trade_review === 'commissioner'
  const faabBudget = fantasySettings?.faab_budget || 100

  return (
    <div className="px-4 py-6 md:py-8 md:px-6">
      <div className="text-center mb-5">
        <div className="text-sm text-text-muted mb-1">No transactions yet.</div>
        <div className="text-base font-semibold text-text-primary">While you're here, here's how transactions work in this league.</div>
      </div>
      <div className="max-w-xl mx-auto">
        <p className="text-sm text-text-primary mb-5">This tab shows all league transactions — trades, waiver claims, free agent pickups, and drops. Once the first move is made, this guide disappears and the activity feed takes over.</p>
        <ul className="space-y-3 text-sm text-text-primary list-none">
          <li className="flex gap-3">
            <span className="text-accent font-bold shrink-0 mt-0.5">→</span>
            <div>
              <span className="font-semibold">Trades</span> — Propose a trade to any manager by selecting players from both rosters. {isCommReview ? 'Trades require commissioner approval before they process.' : 'Once accepted, trades process immediately.'}
            </div>
          </li>
          <li className="flex gap-3">
            <span className="text-accent font-bold shrink-0 mt-0.5">→</span>
            <div>
              <span className="font-semibold">Waivers</span> — When a player is dropped, they go on waivers for 24 hours. During that window, any manager can place a claim. {isFaab
                ? `Claims are resolved by blind bid — you have a $${faabBudget} budget for the season. Highest bid wins the player.`
                : 'Claims are resolved by waiver priority — lower priority number wins. After a successful claim, you move to the back of the line.'}
            </div>
          </li>
          <li className="flex gap-3">
            <span className="text-accent font-bold shrink-0 mt-0.5">→</span>
            <div>
              <span className="font-semibold">Free Agents</span> — Players not on any roster and not on waivers are free agents. You can pick them up instantly — no waiting, first come first served.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="text-accent font-bold shrink-0 mt-0.5">→</span>
            <div>
              <span className="font-semibold">Drops</span> — Drop a player from your roster to free up a spot. The dropped player enters the waiver wire for 24 hours before becoming a free agent.
            </div>
          </li>
        </ul>
      </div>
    </div>
  )
}

// =====================================================================
// Main Transactions Component
// =====================================================================

export default function FantasyTrades({ league, fantasySettings }) {
  const { profile } = useAuth()
  const { data: trades, isLoading: tradesLoading } = useFantasyTrades(league.id)
  const { data: transactions, isLoading: txnLoading } = useFantasyTransactions(league.id)
  const { data: roster } = useFantasyRoster(league.id)
  const respond = useRespondToTrade(league.id)
  const [showProposeModal, setShowProposeModal] = useState(false)
  const [counterTrade, setCounterTrade] = useState(null)
  const [dropModal, setDropModal] = useState(null) // { tradeId, dropsNeeded }
  const [vetoModal, setVetoModal] = useState(null) // { tradeId }
  // Persistent modal (not a toast) shown to the commissioner when approve
  // fails because one side of the trade would go over the roster cap. The
  // affected user needs to drop players before the commish can retry.
  const [rosterOverCapModal, setRosterOverCapModal] = useState(null) // { targetUserName, dropsNeeded }
  // Player detail modal opened from a tap on a transaction-row headshot.
  // ctx mirrors PlayerDetailModal's playerContext values — for now we use
  // 'free_agent' on drop transactions (showing the Add button) and null
  // (info-only) for everything else.
  const [detailPlayer, setDetailPlayer] = useState(null) // { playerId, ctx }
  const addDrop = useAddDropPlayer(league.id)
  const handleTxnPlayerTap = (playerId, txnType) => {
    const isDropType = txnType === 'drop' || txnType === 'waiver_drop'
    setDetailPlayer({ playerId, ctx: isDropType ? 'free_agent' : null })
  }
  const subtab = new URLSearchParams(window.location.search).get('subtab')
  const [activeView, setActiveView] = useState(subtab === 'trades' ? 'trades' : 'activity') // 'activity' | 'trades'

  const isLoading = tradesLoading || txnLoading

  const isCommissioner = league.commissioner_id === profile?.id
  const pending = (trades || []).filter((t) => t.status === 'pending' || t.status === 'pending_review')

  async function handleAction(tradeId, action, dropPlayerIds, reason) {
    try {
      await respond.mutateAsync({ tradeId, action, drop_player_ids: dropPlayerIds, reason })
      // Negative outcomes (veto / decline) get the red error toast — they
      // succeeded technically but they're killing a trade, not approving one.
      const tone = action === 'veto' || action === 'decline' ? 'error' : 'success'
      toast(`Trade ${pastTense(action)}`, tone)
      setDropModal(null)
      setVetoModal(null)
    } catch (err) {
      const body = err?.response || err
      if (body?.requires_drop && action === 'accept') {
        setDropModal({ tradeId, dropsNeeded: body.drops_needed || 1 })
      } else if (body?.roster_over_cap && action === 'approve') {
        // Persistent modal, not a toast — commissioner needs to read + act.
        setRosterOverCapModal({
          targetUserName: body.target_user_name || 'That manager',
          dropsNeeded: body.drops_needed || 1,
        })
      } else {
        toast(err.message || `Failed to ${action} trade`, 'error')
      }
    }
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs: Activity | Trades | + Propose Trade */}
      <div className="flex gap-1 items-center">
        {['activity', 'trades'].map((v) => (
          <button
            key={v}
            onClick={() => setActiveView(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              activeView === v ? 'bg-accent text-white' : 'bg-bg-primary border border-text-primary/20 text-text-secondary'
            }`}
          >
            {v === 'activity' ? 'Activity' : `Trades${pending.length ? ` (${pending.length})` : ''}`}
          </button>
        ))}
        <button
          onClick={() => setShowProposeModal(true)}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
        >
          + Propose Trade
        </button>
      </div>
      {showProposeModal && (
        <ProposeTradeModal league={league} currentUserId={profile?.id} onClose={() => setShowProposeModal(false)} />
      )}
      {counterTrade && (
        <ProposeTradeModal
          league={league}
          currentUserId={profile?.id}
          initialReceiverId={counterTrade.proposer_user_id}
          counteringTradeId={counterTrade.id}
          onClose={() => setCounterTrade(null)}
        />
      )}

      {isLoading ? (
        <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div>
      ) : activeView === 'activity' ? (
        /* Activity log */
        <div className="rounded-xl border border-text-primary/20 bg-bg-primary/60 overflow-hidden">
          {!transactions?.length ? (
            <TransactionsOnboarding fantasySettings={fantasySettings} />
          ) : (
            <div className="px-4 bg-bg-primary/30 rounded-xl border border-text-primary/10">
              {(() => {
                // Group trade_send/trade_receive by trade_id into single entries
                const rendered = new Set()
                const items = []
                for (const txn of transactions) {
                  if ((txn.type === 'trade_send' || txn.type === 'trade_receive') && txn.trade_id) {
                    if (rendered.has(txn.trade_id)) continue
                    rendered.add(txn.trade_id)
                    const tradeItems = transactions.filter((t) => t.trade_id === txn.trade_id)
                    items.push(
                      <TradeTransactionRow key={`trade-${txn.trade_id}`} items={tradeItems} timestamp={txn.created_at} />
                    )
                  } else {
                    items.push(<TransactionRow key={txn.id} txn={txn} onTapPlayer={handleTxnPlayerTap} />)
                  }
                }
                return items
              })()}
            </div>
          )}
        </div>
      ) : (
        /* Trades view */
        <div className="space-y-3">
          {pending.length > 0 && (
            <div>
              <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">Pending</h3>
              <div className="space-y-3">
                {pending.map((t) => (
                  <TradeCard key={t.id} trade={t} currentUserId={profile?.id} isCommissioner={isCommissioner}
                    onAccept={(id) => handleAction(id, 'accept')}
                    onDecline={(id) => handleAction(id, 'decline')}
                    onCancel={(id) => handleAction(id, 'cancel')}
                    onCounter={(trade) => setCounterTrade(trade)}
                    onApprove={(id) => handleAction(id, 'approve')}
                    onVeto={(id) => setVetoModal({ tradeId: id })} />
                ))}
              </div>
            </div>
          )}
          {(trades || []).filter((t) => t.status !== 'pending' && t.status !== 'pending_review').length > 0 && (
            <div>
              <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">History</h3>
              <div className="space-y-3">
                {(trades || []).filter((t) => t.status !== 'pending' && t.status !== 'pending_review').map((t) => (
                  <TradeCard key={t.id} trade={t} currentUserId={profile?.id} isCommissioner={isCommissioner} onAccept={() => {}} onDecline={() => {}} onCancel={() => {}} />
                ))}
              </div>
            </div>
          )}
          {!(trades || []).length && (
            <div className="text-center py-8 text-sm text-text-muted">No trades yet.</div>
          )}
        </div>
      )}

      {dropModal && (() => {
        const trade = (trades || []).find((t) => t.id === dropModal.tradeId)
        if (!trade) return null
        return (
          <TradeDropModal
            roster={roster}
            trade={trade}
            dropsNeeded={dropModal.dropsNeeded}
            onConfirm={(dropIds) => handleAction(dropModal.tradeId, 'accept', dropIds)}
            onCancel={() => setDropModal(null)}
            isPending={respond.isPending}
          />
        )
      })()}

      {vetoModal && (
        <VetoTradeModal
          onConfirm={(reason) => handleAction(vetoModal.tradeId, 'veto', null, reason)}
          onCancel={() => setVetoModal(null)}
          isPending={respond.isPending}
        />
      )}

      {rosterOverCapModal && (
        <RosterOverCapModal
          targetUserName={rosterOverCapModal.targetUserName}
          dropsNeeded={rosterOverCapModal.dropsNeeded}
          onClose={() => setRosterOverCapModal(null)}
        />
      )}

      {detailPlayer && (
        <PlayerDetailModal
          leagueId={league.id}
          playerId={detailPlayer.playerId}
          playerContext={detailPlayer.ctx}
          onClose={() => setDetailPlayer(null)}
          onAdd={async (pid) => {
            try {
              await addDrop.mutateAsync({ addPlayerId: pid, dropPlayerId: null })
              toast('Player added', 'success')
              setDetailPlayer(null)
            } catch (err) {
              // Common case: roster is full and a drop is needed. Direct
              // the user to the Players tab where the full add-drop flow
              // lives. Other errors (player not actually a free agent,
              // on waivers, etc.) just toast the message.
              const msg = err?.message || 'Failed to add player'
              if (/full|drop/i.test(msg)) {
                toast('Roster is full — head to the Players tab to add with a drop', 'error')
              } else {
                toast(msg, 'error')
              }
            }
          }}
        />
      )}
    </div>
  )
}

// Lightweight modal for the commissioner's optional veto rationale.
function VetoTradeModal({ onConfirm, onCancel, isPending }) {
  const [reason, setReason] = useState('')
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-bg-primary border border-text-primary/20 rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg mb-2">Veto this trade?</h3>
        <p className="text-sm text-text-muted mb-4">
          Optional — leave a note so league members understand the call. They'll see this on the vetoed trade in History.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="Reason (optional)…"
          rows={3}
          maxLength={500}
          className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent resize-none"
        />
        <div className="text-[10px] text-text-muted text-right mt-1">{reason.length}/500</div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-border disabled:opacity-50"
          >Cancel</button>
          <button
            onClick={() => onConfirm(reason.trim() || null)}
            disabled={isPending}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-incorrect text-white disabled:opacity-50"
          >{isPending ? 'Vetoing…' : 'Veto Trade'}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Persistent modal (not a toast) shown to the commissioner when approve
// fails because one side of the trade would go over the roster cap. Names
// the affected user and how many players they need to drop so the commish
// can pass the message along.
function RosterOverCapModal({ targetUserName, dropsNeeded, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-primary border border-text-primary/20 rounded-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h3 className="font-display text-lg">Trade can't be approved yet</h3>
        </div>
        <div className="rounded-xl border border-text-primary/20 bg-text-primary/5 p-3">
          <p className="text-sm text-text-primary leading-relaxed">
            <span className="font-semibold">{targetUserName}</span> is over the roster cap by <span className="font-semibold">{dropsNeeded}</span> player{dropsNeeded > 1 ? 's' : ''}.
          </p>
          <p className="text-sm text-text-primary/80 leading-relaxed mt-2">
            Their roster changed after this trade was set up. Ask them to drop {dropsNeeded === 1 ? 'a player' : `${dropsNeeded} players`} from the Players tab, then come back and approve.
          </p>
          <p className="text-xs text-text-muted leading-relaxed mt-3">
            Alternatively, veto this trade if you don't want to wait for the drop.
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
        >
          OK, got it
        </button>
      </div>
    </div>,
    document.body
  )
}
